import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { superAdminAPI } from '../../services/api';
import {
  FilterSelect, ReportFilterBar, EMPTY_REPORT_FILTERS, filterReportRows, uniqueSorted,
} from '../../components/SuperAdmin/ListTools';
import { formatExportDate } from '../../utils/exportData';
import {
  HiOutlineUserGroup,
  HiOutlineRefresh,
  HiOutlineMail,
} from 'react-icons/hi';
import { ReportGuide, periodLabel } from '../../components/SuperAdmin/ReportGuide';

const formatDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

const PERIODS = [7, 30, 90, 180, 365];

const formatHours = (h) => {
  if (h == null) return '—';
  if (h < 1) return `${Math.round(h * 60)}m`;
  if (h < 48) return `${h}h`;
  return `${Math.round((h / 24) * 10) / 10}d`;
};

const CustomerWiseTicketReport = () => {
  const [days, setDays] = useState(30);
  const [filters, setFilters] = useState({ ...EMPTY_REPORT_FILTERS, flag: '' });

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['customer-ticket-report', days],
    queryFn: () => superAdminAPI.getCustomerTicketReport({ days }),
    refetchInterval: 120000,
  });

  // Axios → res.data (server body) → body.data
  const body = data?.data?.data || {};
  const stats = body.stats || {};
  const allRows = Array.isArray(body.rows) ? body.rows : [];

  // A customer can span several organizations, so match the org filter against the list
  const rows = filterReportRows(allRows, { ...filters, organization: '' }, {
    searchOf: (r) => [r.customerName, r.customerEmail, r.customerPhone, ...(r.organizations || [])],
  }).filter((r) =>
    (!filters.organization || (r.organizations || []).includes(filters.organization))
    && (!filters.flag
      || (filters.flag === 'open' && r.open > 0)
      || (filters.flag === 'breaches' && r.breaches > 0)
      || (filters.flag === 'urgent' && r.urgentHigh > 0)));

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex-1 basis-80">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <HiOutlineUserGroup className="w-7 h-7 text-primary-500" />
            Tickets by Customer
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Which customers raise the most tickets and how fast we solve them
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-xs font-medium text-gray-500">Show last</span>
          <div className="flex flex-wrap gap-1 bg-gray-100 rounded-xl p-1">
            {PERIODS.map((p) => (
              <button
                key={p}
                onClick={() => setDays(p)}
                className={`px-3.5 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  days === p ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {periodLabel(p)}
              </button>
            ))}
          </div>
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-primary-500 text-white text-sm font-medium
                       hover:bg-primary-600 shadow-lg shadow-primary-500/25 disabled:opacity-50"
          >
            <HiOutlineRefresh className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      <ReportGuide
        id="customer-tickets"
        summary="Find customers who need the most help, or who are having repeated problems."
        points={[
          ["Tickets", "Tickets this customer raised in the period."],
          ["Urgent/High", "How many of those were high priority."],
          ["Avg resolution", "Typical time to solve their tickets."],
          ["Missed deadlines", "Their tickets that were not handled in the promised time."],
        ]}
      />

      {/* Stat tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Customers</p>
          <p className="text-3xl font-bold text-gray-900 mt-2">{stats.totalCustomers ?? 0}</p>
          <p className="text-xs text-gray-400 mt-1">Raised at least one ticket</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Total Tickets</p>
          <p className="text-3xl font-bold text-primary-500 mt-2">{stats.totalTickets ?? 0}</p>
          <p className="text-xs text-gray-400 mt-1">Last {body.windowDays ?? days} days</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Open</p>
          <p className="text-3xl font-bold text-amber-500 mt-2">{stats.openTickets ?? 0}</p>
          <p className="text-xs text-gray-400 mt-1">Awaiting resolution</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Resolved</p>
          <p className="text-3xl font-bold text-green-500 mt-2">{stats.resolvedTickets ?? 0}</p>
          <p className="text-xs text-gray-400 mt-1">Successfully closed out</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Avg Resolution</p>
          <p className="text-3xl font-bold text-gray-900 mt-2">{formatHours(stats.avgResolutionHours)}</p>
          <p className="text-xs text-gray-400 mt-1">Across all customers</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Tickets / Customer</p>
          <p className="text-3xl font-bold text-gray-900 mt-2">{stats.avgTicketsPerCustomer ?? 0}</p>
          <p className="text-xs text-gray-400 mt-1">Average volume</p>
        </div>
      </div>

      {/* Customers table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
          <h2 className="text-sm font-semibold text-gray-700">Customers</h2>
          <span className="text-xs text-gray-400">
            {rows.length === allRows.length ? `${rows.length} shown` : `${rows.length} of ${allRows.length} shown`}
          </span>
        </div>

        {allRows.length > 0 && (
          <ReportFilterBar
            filters={filters}
            onChange={setFilters}
            showPriority={false}
            searchPlaceholder="Search customer name, email, phone…"
            organizations={uniqueSorted(allRows.flatMap((r) => r.organizations || []))}
            extraActive={!!filters.flag}
            onResetExtra={() => setFilters((f) => ({ ...f, flag: '' }))}
            exportProps={{
              fileName: `customer-tickets-${days}d`,
              getRows: () => rows,
              columns: [
                { header: 'Customer', value: (r) => r.customerName || 'Unknown' },
                { header: 'Email', value: (r) => r.customerEmail },
                { header: 'Phone', value: (r) => r.customerPhone },
                { header: 'Organizations', value: (r) => r.organizations || [] },
                { header: 'Total Tickets', value: (r) => r.totalTickets },
                { header: 'Open', value: (r) => r.open },
                { header: 'Resolved', value: (r) => r.resolved },
                { header: 'Closed', value: (r) => r.closed },
                { header: 'Urgent/High', value: (r) => r.urgentHigh },
                { header: 'Avg Resolution', value: (r) => formatHours(r.avgResolutionHours) },
                { header: 'SLA Breaches', value: (r) => r.breaches },
                { header: 'Last Ticket', value: (r) => formatExportDate(r.lastTicketAt) },
              ],
            }}
          >
            <FilterSelect
              value={filters.flag}
              onChange={(v) => setFilters((f) => ({ ...f, flag: v }))}
              allLabel="All Customers"
              options={[['open', 'Has open tickets'], ['breaches', 'Has SLA breaches'], ['urgent', 'Has urgent/high tickets']]}
            />
          </ReportFilterBar>
        )}

        {isLoading ? (
          <div className="p-8 text-center text-gray-400 text-sm">Loading customer-wise ticket report…</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-gray-400">
            {allRows.length > 0 ? 'No customers match these filters' : 'No customer tickets in this period'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wider text-gray-500">
                  <th className="px-4 py-3">Customer</th>
                  <th className="px-4 py-3">Organizations</th>
                  <th className="px-4 py-3">Tickets</th>
                  <th className="px-4 py-3">Open</th>
                  <th className="px-4 py-3">Resolved</th>
                  <th className="px-4 py-3">Closed</th>
                  <th className="px-4 py-3">Urgent/High</th>
                  <th className="px-4 py-3">Avg Resolution</th>
                  <th className="px-4 py-3">Missed Deadlines</th>
                  <th className="px-4 py-3">Last Ticket</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((row, i) => (
                  <tr key={`${row.customerEmail || row.customerPhone || row.customerName}-${i}`} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-900">{row.customerName || 'Unknown'}</p>
                      {row.customerEmail && (
                        <p className="text-xs text-gray-400 flex items-center gap-1">
                          <HiOutlineMail className="w-3 h-3" />
                          {row.customerEmail}
                        </p>
                      )}
                      {row.customerPhone && (
                        <p className="text-xs text-gray-400">{row.customerPhone}</p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-xs text-gray-600 max-w-[160px] truncate" title={(row.organizations || []).join(', ')}>
                        {(row.organizations || []).join(', ') || '—'}
                      </p>
                    </td>
                    <td className="px-4 py-3 font-semibold text-gray-900">{row.totalTickets}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                        row.open > 0 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-400'
                      }`}>
                        {row.open}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-green-600 font-medium">{row.resolved}</td>
                    <td className="px-4 py-3 text-gray-600">{row.closed}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-medium ${row.urgentHigh > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                        {row.urgentHigh}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-medium text-gray-900">{formatHours(row.avgResolutionHours)}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-semibold ${row.breaches > 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {row.breaches}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-xs">{formatDate(row.lastTicketAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerWiseTicketReport;